import { Injectable } from '@angular/core';
import { ProductTabsService } from 'src/app/services/product_windows/products-tabs.service';
import { DEFAULT_TEXT_FIELD_DATA, PDFService } from 'src/app/services/PDF/pdf.service';
import { EntrySelectionService } from 'src/app/services/entry-selection/entry-selection.service';
import { ConfiguratorStateService } from 'src/app/services/configurator-state/configurator-state.service';
import { WindowsStore } from 'src/app/services/akita/tabs.store';
import { CategoryStore } from 'src/app/services/akita/category.store';
import { CategoryContentStore } from 'src/app/services/akita/category-content.store';
import { MaterialStore } from 'src/app/services/akita/materials/materials.store';
import { MaterialRuleStore } from 'src/app/services/akita/materialRules/materialRule.store';
import { RuleStore } from 'src/app/services/akita/rules/rules.store';
import { ImageStore } from 'src/app/components/layers/services/images.store';
import { DefaultSelectionStore } from 'src/app/services/final_product/akita/DefaultOptions/default.store';

/** Per-configuration keys the PDF window reads out of web storage. */
export const PDF_SESSION_KEYS = [
  'pdfData',
  'materialValuesData',
  'textFieldData',
  'selected',
  'selectedToolTypes',
  'ccQuantities',
  'ccDesignLines',
  'codeQuantities',
];

/**
 * Clears one configuration's worth of state.
 *
 * Before the cart existed, the app only ever held one configuration per
 * session, so nothing ever needed clearing — the single `setSelectedData([])`
 * in HomeComponent was the whole story. With several configurations in a
 * project, everything that was previously allowed to accumulate now bleeds
 * from one cart item into the next.
 *
 * This deliberately lives outside ProductTabsService: that service already
 * takes sixteen constructor dependencies and injecting the Akita stores and
 * PDFService into it risks a cycle with NavBarService.
 */
@Injectable({ providedIn: 'root' })
export class ConfiguratorResetService {
  constructor(
    private productWindowService: ProductTabsService,
    private pdfService: PDFService,
    private entrySelection: EntrySelectionService,
    private configuratorState: ConfiguratorStateService,
    private windowsStore: WindowsStore,
    private categoryStore: CategoryStore,
    private categoryContentStore: CategoryContentStore,
    private materialStore: MaterialStore,
    private materialRuleStore: MaterialRuleStore,
    private ruleStore: RuleStore,
    private imageStore: ImageStore,
    private defaultSelectionStore: DefaultSelectionStore
  ) { }

  /**
   * Clear the user's work while keeping the current product's reference data
   * loaded. Use when finishing a configuration on the same final product.
   */
  resetConfiguration(): void {
    this.productWindowService.resetConfigurationState();

    this.pdfService.setSelectedTemplates([]);
    this.pdfService.setTextFieldData({ ...DEFAULT_TEXT_FIELD_DATA });

    this.entrySelection.clear();

    // Also signals any live NavbarComponent to drop its local copy.
    this.configuratorState.clear();

    this.clearConfigurationStorage();
  }

  /**
   * As above, plus the Akita stores that are keyed to — or gated on — a single
   * final product. Without this, starting a configuration on a different
   * product short-circuits against the previous product's cached tabs,
   * categories, materials, rules and layer images.
   */
  resetForNewFinalProduct(): void {
    this.resetConfiguration();

    this.windowsStore.reset();
    this.categoryStore.reset();
    this.categoryContentStore.reset();
    this.materialStore.reset();
    this.materialRuleStore.reset();
    this.ruleStore.reset();
    this.imageStore.reset();
    this.defaultSelectionStore.reset();

    // FinalProductStore is deliberately NOT reset: it is keyed by product id,
    // holds layer and background definitions, and is expensive to refill.
    // ColorStore holds the theme and is not per-configuration either.
  }

  /**
   * The single-slot web-storage keys the PDF flow uses. They are namespaced
   * per configuration when a PDF is generated; these are the legacy unprefixed
   * ones, cleared so a stale configuration can never leak into a new PDF.
   */
  private clearConfigurationStorage(): void {
    localStorage.removeItem('carouselImage');
    PDF_SESSION_KEYS.forEach(key => sessionStorage.removeItem(key));
  }
}
